import React from 'react';
import api from '../../../config/api';

const VideoSection = ({ videos, onVideoSelect }) => {
    const [videoPreviews, setVideoPreviews] = React.useState({});
    const videoPreviewsRef = React.useRef(videoPreviews);

    React.useEffect(() => {
        videoPreviewsRef.current = videoPreviews;
    }, [videoPreviews]);

    React.useEffect(() => {
        let cancelled = false;

        async function run() {
            const urls = videos.map((v) => v.url).filter(Boolean);
            if (!urls.length) return;

            const current = videoPreviewsRef.current || {};
            const missing = urls.filter((u) => !current[String(u)]);
            if (!missing.length) return;

            try {
                const results = await Promise.allSettled(
                    missing.map((u) => api.get('/api/profiles/video-preview', { params: { url: u } }))
                );

                if (cancelled) return;

                const updates = {};
                for (const r of results) {
                    if (r.status !== 'fulfilled') continue;
                    const d = r.value?.data;
                    const url = d?.url;
                    if (!url) continue;
                    updates[String(url)] = {
                        platform: d?.platform || null,
                        title: d?.title || null,
                        thumbnail_url: d?.thumbnail_url || null
                    };
                }

                const keys = Object.keys(updates);
                if (keys.length) {
                    setVideoPreviews((prev) => ({
                        ...(prev || {}),
                        ...updates
                    }));
                }
            } catch (_) {
                /* noop */
            }
        }

        run();
        return () => {
            cancelled = true;
        };
    }, [videos]);

    return (
        <div className="rounded-2xl bg-white border border-secondary-200 shadow-soft p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="text-sm font-semibold text-secondary-900">Видео</div>
                <div className="text-xs text-secondary-500">{videos.length} / 3</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((v) => {
                    const preview = videoPreviews[String(v.url)] || null;
                    const thumb = preview?.thumbnail_url || null;
                    const platformKey = String(preview?.platform || v?.platform || '').toLowerCase();
                    const isVk = platformKey === 'vk' || /\bvk\b/i.test(String(v?.label || ''));
                    const isRutube = platformKey === 'rutube' || /rutube/i.test(String(v?.label || ''));

                    const placeholderBgClass = isVk
                        ? 'bg-gradient-to-br from-blue-600/70 via-blue-900/70 to-black'
                        : isRutube
                            ? 'bg-gradient-to-br from-orange-500/70 via-red-600/60 to-black'
                            : 'bg-gradient-to-br from-secondary-600/60 via-secondary-900/70 to-black';

                    const badgeText = isVk ? 'VK' : isRutube ? 'Rutube' : 'Video';

                    return (
                        <button
                            key={v.id || v.label}
                            type="button"
                            className="rounded-2xl border border-secondary-200 bg-secondary-50 overflow-hidden text-left hover:shadow-soft transition"
                            onClick={() => onVideoSelect(v)}
                            title="Открыть видео"
                        >
                            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-white border-b border-secondary-200">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-secondary-900 truncate">{v.label}</div>
                                </div>
                                <span className="text-xs text-secondary-500">▶</span>
                            </div>

                            <div className="relative w-full pt-[56.25%] bg-black">
                                {thumb ? (
                                    <img
                                        src={thumb}
                                        alt={preview?.title || v.label}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        loading="lazy"
                                        onError={() => {
                                            setVideoPreviews((prev) => {
                                                const key = String(v.url);
                                                const cur = (prev || {})[key] || {};
                                                return {
                                                    ...(prev || {}),
                                                    [key]: {
                                                        ...cur,
                                                        thumbnail_url: null
                                                    }
                                                };
                                            });
                                        }}
                                    />
                                ) : (
                                    <div className={`absolute inset-0 ${placeholderBgClass}`}>
                                        <div
                                            className="absolute inset-0 opacity-30"
                                            style={{
                                                backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.25), transparent 45%)'
                                            }}
                                        />
                                        <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-[11px] font-semibold tracking-wide text-white/90 bg-black/30 border border-white/10">
                                            {badgeText}
                                        </div>
                                    </div>
                                )}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                                        <div className="w-0 h-0 border-y-[10px] border-y-transparent border-l-[16px] border-l-white ml-1" />
                                    </div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default VideoSection;
